/**
 * [BUTTON-STYLE 2026-09-30, user] ONE look for every text button in the app: "D2 Glass gradient",
 * the style the user picked. Three variants that share one shape:
 *
 *   buttonPrimary   - the main green action: green glass gradient, border, inset highlight, soft
 *                     glow and a top sheen, white text (first built as PaymentsPage AddButton).
 *   buttonSecondary - the plain action (Cancel, Download, Bulk ...): glass white, 1px border, text
 *                     colour (first built as InventoryPage DownloadButton).
 *   buttonDanger    - the secondary shape in red (Delete, a destructive confirm).
 *
 * The shared shape: 32px tall, padding 0 14px, 10px radius, 13px / 700 text, inline-flex with a 6px
 * icon gap, never wraps. A component puts the mixin FIRST and keeps only its layout rules (margins,
 * widths, flex, positioning, media queries) after it, so a layout rule still wins.
 *
 * pillTrack / pillSegment - the ONE size for every switch pill (segmented toggle): a fully round
 * track with 3px padding and segments of 7px 18px at 13px / 700, about 30px tall. Each pill keeps
 * its own active colour and sliding-thumb mechanism; only the size and the font come from here.
 *
 * No backticks inside the CSS comments below: one would end the template early
 * (scripts/check-styled-comments.js guards it).
 */
import { css, DefaultTheme } from 'styled-components';

type P = { theme: DefaultTheme };
const isDark = (p: P) => p.theme.colorScheme === 'dark';

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

const buttonShape = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  box-sizing: border-box;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 120ms ${EASE},
    box-shadow 180ms ${EASE},
    filter 120ms ${EASE},
    background 120ms ${EASE},
    border-color 120ms ${EASE},
    opacity 120ms ${EASE};

  &:active:not(:disabled) { transform: translateY(1px); }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

/** Main green action. */
export const buttonPrimary = css`
  ${buttonShape}
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${(p: P) => (isDark(p) ? 'rgba(70, 191, 130, 0.55)' : 'rgba(26, 159, 125, 0.55)')};
  /* [PURE-INK 2026-10-15] Under components/PureInk (theme.primaryDeep) the light theme's fill is the deeper green (greenInk →
     green700) so the white words read at 5 : 1+ (the usual light mint is about 2.5 : 1). Every other page is unchanged. */
  background: ${(p: P) => (isDark(p)
    ? 'linear-gradient(180deg, rgba(70, 191, 130, 0.55) 0%, rgba(61, 158, 108, 0.65) 100%)'
    : p.theme.primaryDeep
      ? 'linear-gradient(180deg, rgba(22, 115, 90, 0.96) 0%, rgba(20, 107, 84, 0.98) 100%)'
      : 'linear-gradient(180deg, rgba(13, 193, 152, 0.85) 0%, rgba(26, 159, 125, 0.92) 100%)')};
  color: #fff;
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  box-shadow:
    0 6px 18px ${(p: P) => (isDark(p) ? 'rgba(61, 158, 108, 0.45)' : 'rgba(26, 159, 125, 0.35)')},
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12);

  /* The glass sheen over the top half. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.10) 45%, rgba(255,255,255,0) 60%);
    pointer-events: none;
    z-index: 0;
  }

  & > * { position: relative; z-index: 1; }

  &:hover:not(:disabled) {
    filter: brightness(1.06);
    box-shadow:
      0 10px 24px ${(p: P) => (isDark(p) ? 'rgba(61, 158, 108, 0.55)' : 'rgba(26, 159, 125, 0.45)')},
      inset 0 1px 0 rgba(255, 255, 255, 0.55),
      inset 0 -1px 0 rgba(0, 0, 0, 0.14);
  }
`;

/** Plain action: glass white, 1px border, text colour. */
export const buttonSecondary = css`
  ${buttonShape}
  border: 1px solid ${(p: P) => (isDark(p) ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
  background: ${(p: P) => (isDark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.65)')};
  color: ${(p: P) => p.theme.text};
  box-shadow: inset 0 1px 0 ${(p: P) => (isDark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.80)')};

  &:hover:not(:disabled) {
    background: ${(p: P) => (isDark(p) ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.85)')};
    border-color: ${(p: P) => (isDark(p) ? 'rgba(70,191,130,0.35)' : 'rgba(26,159,125,0.30)')};
  }
`;

/** The secondary shape in red: Delete and destructive confirms. */
export const buttonDanger = css`
  ${buttonShape}
  border: 1px solid color-mix(in srgb, ${(p: P) => p.theme.danger} 40%, transparent);
  background: color-mix(in srgb, ${(p: P) => p.theme.danger} 10%, transparent);
  color: ${(p: P) => p.theme.danger};
  box-shadow: inset 0 1px 0 ${(p: P) => (isDark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.80)')};

  &:hover:not(:disabled) {
    background: color-mix(in srgb, ${(p: P) => p.theme.danger} 16%, transparent);
    border-color: color-mix(in srgb, ${(p: P) => p.theme.danger} 60%, transparent);
  }
`;

/** Switch-pill track: fully round, 3px padding. Size only; the track keeps its own colours. */
export const pillTrack = css`
  padding: 3px;
  border-radius: 999px;
`;

/** Switch-pill segment: 7px 18px at 13px / 700, about 30px tall. Size and font only. */
export const pillSegment = css`
  padding: 7px 18px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
`;
